import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { GLOBALTYPES } from "../redux/actions/globalTypes";
import { getDataAPI, postDataAPI } from "../utils/fetchData";
import Star from "./star";

// Constants
const GRADES = ["Poor", "Average", "Good", "Very good", "Excellent"];
const ACTIVE_STAR_STYLE = { fill: "yellow" };
const CONTAINER_STYLE = { padding: "0px 100px" };
const FEEDBACK_HEADING_STYLE = { justifyContent: "space-between" };
const FEEDBACK_TEXT_STYLE = { whiteSpace: "pre-line" };

export default function Instructorinfo() {
	const dispatch = useDispatch();
	const { auth } = useSelector((state) => state);

	// State
	const [instructor, setInstructor] = useState({});
	const [show, setShow] = useState(false);
	const [gradeIndex, setGradeIndex] = useState();
	const [loading, setLoading] = useState(false);

	// Modal handlers
	const handleClose = useCallback(() => {
		setShow(false);
		setGradeIndex();
	}, []);

	const handleShow = useCallback(() => setShow(true), []);

	// Star rating handler
	const changeGradeIndex = useCallback((index) => {
		setGradeIndex(index);
	}, []);

	// Fetch instructor data
	useEffect(() => {
		const fetchInstructorData = async () => {
			if (!auth.token) return;

			try {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });
				const instructorId = window.location.pathname.split("/")[2];
				const res = await getDataAPI(
					`getInstructorsById/${instructorId}`,
					auth.token
				);
				setInstructor(res.data.instructor || {});
			} catch (err) {
				console.error("Error fetching instructor data:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { error: "Failed to fetch instructor data" },
				});
			} finally {
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { loading: false },
				});
			}
		};

		fetchInstructorData();
	}, [auth.token, dispatch]);

	// Submit feedback handler
	const handleSubmit = useCallback(
		async (e) => {
			e.preventDefault();

			if (gradeIndex === undefined) {
				alert("Please select a rating before submitting");
				return;
			}

			setLoading(true);
			dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });

			try {
				const res = await postDataAPI(
					"updateinstructorfeedback",
					{
						id: instructor.id,
						feedback: GRADES[gradeIndex],
					},
					auth.token
				);

				if (res.status === 200) {
					alert("Thank you for submitting feedback");
					window.location.reload();
				}
			} catch (err) {
				console.error("Error submitting feedback:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: {
						error: err.response?.data?.msg || "Failed to submit feedback",
					},
				});
			} finally {
				setLoading(false);
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		},
		[gradeIndex, instructor.id, auth.token, dispatch]
	);

	// Memoized components
	const coursesDisplay = useMemo(() => {
		if (!instructor.courses_approved) return "";

		try {
			const courses = JSON.parse(instructor.courses_approved);
			return courses
				.map((course, index) => {
					if (index === 0) return course.coursetitle;
					if (index === courses.length - 1) return course.coursetitle + ".";
					return course.coursetitle + ", ";
				})
				.join("");
		} catch (err) {
			console.error("Error parsing courses:", err);
			return "";
		}
	}, [instructor.courses_approved]);

	const feedbackList = useMemo(() => {
		if (!instructor.feedback || instructor.feedback.length === 0) return null;

		return instructor.feedback.map((feed, index) => (
			<p key={index} style={FEEDBACK_TEXT_STYLE}>
				{index + 1}) {feed}
			</p>
		));
	}, [instructor.feedback]);

	const starComponents = useMemo(() => {
		return GRADES.map((grade, index) => (
			<Star
				index={index}
				key={grade}
				changeGradeIndex={changeGradeIndex}
				style={gradeIndex > index - 1 ? ACTIVE_STAR_STYLE : {}}
			/>
		));
	}, [gradeIndex, changeGradeIndex]);

	return (
		<>
			<Modal show={show} onHide={handleClose}>
				<div className="js">
					<div className="x-style" onClick={handleClose}>
						<span style={{ cursor: "pointer" }}>X</span>
					</div>
					<div className="js-boxIn">
						<div className="js-boxIn-content">
							<div className="ai-greenbox">
								<div>
									<h4 className="js-greenbox-heading">Add Feedback</h4>
								</div>
								<div>
									<form onSubmit={handleSubmit}>
										<div className="container-star">
											<h1 className="result">
												{GRADES[gradeIndex] || "Select Rating"}
											</h1>
											<div className="stars">{starComponents}</div>
										</div>

										<button
											type="submit"
											className="js-submitbtn"
											disabled={loading || gradeIndex === undefined}>
											{loading ? "SUBMITTING..." : "SUBMIT"}
										</button>
									</form>
								</div>
							</div>
						</div>
					</div>
				</div>
			</Modal>

			<div className="att" style={CONTAINER_STYLE}>
				<div className="att-box">
					<h2>{instructor.name || "Loading..."}</h2>
					<p>Email: {instructor.email || "N/A"}</p>
					<p>Mobile: {instructor.mobile || "N/A"}</p>
					<p>Courses Approved: {coursesDisplay || "No courses approved"}</p>

					<div
						className="att-heading h2 mt-3 d-flex"
						style={FEEDBACK_HEADING_STYLE}>
						<p>Feedback</p>
						<button className="addinstructor-btn" onClick={handleShow}>
							+ ADD FEEDBACK
						</button>
					</div>

					{feedbackList}
				</div>
			</div>
		</>
	);
}
